import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { 
  createTranslator,
  getLanguage,
  setLanguage,
  initializeI18n,
  onLanguageChange,
  cleanupI18n,
  getTextDirection,
  normalizeLanguage,
  isTierA,
  isAutoTranslated,
  searchLanguages,
  getLanguageInfo,
  ALL_LANGUAGES,
  TIER_A_LANGUAGES,
  LANGUAGE_MAP,
  // Legacy aliases
  LOCALE_CONFIG,
  LOCALE_INFO,
} from '@/i18n';

// Import and initialize i18next for react-i18next compatibility
import '@/i18n/i18next-init';
import { syncI18nextLanguage } from '@/i18n/i18next-init';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// GLOBAL LANGUAGE STATE (for API interceptors)
// ============================================
let currentLangForApi = 'et';

export const getCurrentLocale = () => currentLangForApi;
export const setCurrentLocale = (lang) => {
  currentLangForApi = lang;
  // Sync with i18next
  syncI18nextLanguage(lang);
};

// ============================================
// AXIOS LANGUAGE INTERCEPTORS
// Add Accept-Language and X-Keikka-Lang headers to EVERY request
// ============================================
axios.interceptors.request.use((config) => {
  const lang = getCurrentLocale();
  config.headers['Accept-Language'] = lang;
  config.headers['X-Keikka-Lang'] = lang;
  config.headers['X-Locale'] = lang; // Legacy support
  return config;
}, (error) => Promise.reject(error));

// Response interceptor
axios.interceptors.response.use((response) => {
  if (process.env.NODE_ENV === 'development') {
    const contentLang = response.headers['content-language'];
    if (contentLang) {
      console.log(`[API] Response Content-Language: ${contentLang}`);
    }
  }
  return response;
}, (error) => Promise.reject(error));

// ============================================
// AUTH CONTEXT
// ============================================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('keikka_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      // Note: We do NOT override locale from user profile on login
      // The user's localStorage/cookie choice takes precedence
      // This prevents locale from resetting on login/logout
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('keikka_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Note: Locale is NOT changed on login - user's choice persists
    return userData;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = res.data;
    localStorage.setItem('keikka_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Note: Locale is NOT changed on register - user's choice persists
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('keikka_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    // Note: Locale is NOT changed on logout - user's choice persists
  };

  const updateLanguage = async (lang) => {
    if (user) {
      try {
        await axios.put(`${API}/auth/language?language=${lang}`);
        setUser({ ...user, preferred_language: lang });
      } catch (e) {
        console.warn('Failed to update language preference on server:', e);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateLanguage }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// LANGUAGE CONTEXT - Unified LocaleManager v8.0
// All 7 supported languages with full translations
// ============================================
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Initialize language SYNCHRONOUSLY before first render
  const [language, setLanguageState] = useState(() => {
    const detected = initializeI18n();
    setCurrentLocale(detected); // Sync to API interceptor
    return detected;
  });
  
  const isInitialized = useRef(false);

  // Subscribe to cross-tab language changes ONCE. Previously this effect
  // depended on `language`, which meant every language change would:
  //   1. run cleanup → cleanupI18n() → tear down globals
  //   2. re-run the effect → resubscribe onLanguageChange
  // The cleanup/re-init cycle caused the whole tree to flash & remount
  // images on every switch (user reported "vibration"). We now subscribe
  // once on mount and use a functional setState to avoid the closure
  // needing `language`.
  useEffect(() => {
    const unsubscribe = onLanguageChange((newLang) => {
      setLanguageState((prev) => {
        if (prev === newLang) return prev;
        setCurrentLocale(newLang);
        return newLang;
      });
    });
    isInitialized.current = true;
    return () => {
      unsubscribe();
      // NOTE: cleanupI18n() intentionally NOT called here — this effect
      // only runs on mount/unmount of the LanguageProvider, so tearing
      // down i18n during a normal language switch caused the flash.
      cleanupI18n();
    };
  }, []);

  // Handle language change - accepts all supported languages.
  // Not dependent on `language` so consumers using setLanguage inside
  // their own effect deps don't cascade an extra render each switch.
  const handleSetLanguage = useCallback((lang) => {
    const normalized = normalizeLanguage(lang);
    if (!normalized) {
      console.warn(`[LanguageProvider] Unsupported language: ${lang}`);
      return;
    }

    setLanguageState((prev) => {
      if (prev === normalized) return prev; // no-op, no re-render
      setCurrentLocale(normalized);
      setLanguage(normalized, { updateUrl: false, broadcast: true });
      return normalized;
    });
  }, []);

  // Create translator for current language
  const t = useMemo(() => {
    return createTranslator(language);
  }, [language]);

  // Search languages
  const handleSearchLanguages = useCallback((query) => {
    return searchLanguages(query);
  }, []);

  // Get language names from LANGUAGE_MAP (has both nativeName and name)
  const languageNames = useMemo(() => {
    const names = {};
    Object.entries(LANGUAGE_MAP).forEach(([code, info]) => {
      names[code] = info.nativeName || info.nativeLabel;
    });
    return names;
  }, []);

  const languageEnglishNames = useMemo(() => {
    const names = {};
    Object.entries(LANGUAGE_MAP).forEach(([code, info]) => {
      names[code] = info.name || info.label;
    });
    return names;
  }, []);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: handleSetLanguage, 
      t, 
      availableLanguages: TIER_A_LANGUAGES,
      allLanguages: TIER_A_LANGUAGES,
      tierALanguages: TIER_A_LANGUAGES,
      languageNames,
      languageEnglishNames,
      searchLanguages: handleSearchLanguages,
      getLanguageInfo,
      isTierA,
      isAutoTranslated: false,
      getTextDirection,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// ============================================
// API HELPER
// ============================================
export const api = {
  // Generic HTTP methods (path is API-relative, e.g. "/jobs/drafts/mine")
  get: (path, config) => axios.get(`${API}${path}`, config),
  post: (path, data, config) => axios.post(`${API}${path}`, data, config),
  put: (path, data, config) => axios.put(`${API}${path}`, data, config),
  patch: (path, data, config) => axios.patch(`${API}${path}`, data, config),
  delete: (path, config) => axios.delete(`${API}${path}`, config),

  // Jobs
  getJobs: (params) => axios.get(`${API}/jobs`, { params }),
  getJob: (id) => axios.get(`${API}/jobs/${id}`),
  createJob: (data) => axios.post(`${API}/jobs`, data),
  applyToJob: (jobId, data) => axios.post(`${API}/jobs/${jobId}/apply`, data),
  getJobApplications: (jobId) => axios.get(`${API}/jobs/${jobId}/applications`),
  getMyJobs: () => axios.get(`${API}/my-jobs`),
  getMyApplications: () => axios.get(`${API}/my-applications`),
  // Company-side application actions (free — no €2.99 unlock)
  updateApplicationStatus: (jobId, appId, status) =>
    axios.put(`${API}/jobs/${jobId}/applications/${appId}/status`, { status }),
  markApplicationRead: (appId) =>
    axios.post(`${API}/applications/company/${appId}/mark-read`),
  downloadApplicantCv: (appId, { inline = false } = {}) =>
    axios.get(`${API}/applications/company/${appId}/cv${inline ? '?inline=1' : ''}`, {
      responseType: 'blob',
    }),
  
  // Guest Applications (no auth required)
  guestApply: (formData) => axios.post(`${API}/applications/guest`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getGuestApplicationStatus: (applicationId, email) => 
    axios.get(`${API}/applications/guest/${applicationId}?email=${encodeURIComponent(email)}`),
  
  // Capability Card
  getCapabilityCard: () => axios.get(`${API}/capability-card`),
  createCapabilityCard: (data) => axios.post(`${API}/capability-card`, data),
  generateCV: () => axios.post(`${API}/capability-card/generate-cv`),
  
  // Company
  getCompanyProfile: () => axios.get(`${API}/company-profile`),
  updateCompanyProfile: (data) => axios.put(`${API}/company-profile`, data),
  getCompany: (id) => axios.get(`${API}/companies/${id}`),
  getCompanies: (params) => axios.get(`${API}/companies`, { params }),
  
  // Company Logo
  uploadCompanyLogo: (formData) => axios.post(`${API}/companies/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteCompanyLogo: () => axios.delete(`${API}/companies/logo`),
  getCompanyLogoUrl: (logoId) => `${API}/companies/logo/${logoId}`,
  
  // CV Enhancement (AI)
  enhanceCv: (data) => axios.post(`${API}/cv/enhance`, data),
  uploadAndEnhanceCv: (formData) => axios.post(`${API}/cv/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Service Requests
  createServiceRequest: (data) => axios.post(`${API}/service-requests`, data),
  getServiceRequests: (params) => axios.get(`${API}/service-requests`, { params }),
  
  // Auto Repair
  createAutoRepairRequest: (data) => axios.post(`${API}/auto-repair`, data),
  getAutoRepairRequests: (params) => axios.get(`${API}/auto-repair`, { params }),
  
  // Tool Rentals
  createToolListing: (data) => axios.post(`${API}/tool-rentals`, data),
  getToolRentals: (params) => axios.get(`${API}/tool-rentals`, { params }),
  getToolListing: (id) => axios.get(`${API}/tool-rentals/${id}`),
  
  // Ratings
  createRating: (data) => axios.post(`${API}/ratings`, data),
  getUserRatings: (userId, params) => axios.get(`${API}/users/${userId}/ratings`, { params }),
  
  // Translation
  translate: (content, targetLang, contentType, contentId) => 
    axios.post(`${API}/translate`, null, {
      params: { content, target_language: targetLang, content_type: contentType, content_id: contentId }
    }),
  
  // Notifications
  getNotifications: (params) => axios.get(`${API}/notifications`, { params }),
  markNotificationRead: (id) => axios.post(`${API}/notifications/${id}/read`),
  markAllNotificationsRead: () => axios.post(`${API}/notifications/read-all`),
  getNotificationStatus: () => axios.get(`${API}/notifications/status`),
  getNotificationPreferences: () => axios.get(`${API}/notifications/preferences`),
  updateNotificationPreferences: (data) => axios.put(`${API}/notifications/preferences`, data),
  
  // Payments
  createSubscription: (planType) => axios.post(`${API}/payments/create-subscription?plan_type=${planType}`),
  createUnlock: (unlockType, targetId) => axios.post(`${API}/payments/create-unlock`, { unlock_type: unlockType, target_id: targetId }),
  getPaymentStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`),
  
  // Monetization
  getSubscriptionPlans: (planType) => axios.get(`${API}/monetization/plans`, { params: planType ? { plan_type: planType } : {} }),
  getSubscription: () => axios.get(`${API}/monetization/subscription`),
  createMonetizationSubscription: (data) => axios.post(`${API}/monetization/subscription`, data),
  cancelSubscription: () => axios.delete(`${API}/monetization/subscription`),
  getUsageMetrics: (period) => axios.get(`${API}/monetization/usage`, { params: { period: period || 'current_month' } }),
  getNotificationPrefs: () => axios.get(`${API}/monetization/notifications/preferences`),
  updateNotificationPrefs: (data) => axios.put(`${API}/monetization/notifications/preferences`, data),
  checkUnlockStatus: (userId) => axios.get(`${API}/monetization/unlock/check/${userId}`),
  unlockContact: (data) => axios.post(`${API}/monetization/unlock`, data),
  getUnlockHistory: (limit) => axios.get(`${API}/monetization/unlock/history`, { params: { limit: limit || 50 } }),
  
  // Account
  deleteAccount: () => axios.delete(`${API}/account`),
  exportData: () => axios.get(`${API}/account/export`),
  
  // Equipment Rentals
  getRentalItems: (params) => axios.get(`${API}/rentals/items`, { params }),
  getRentalItem: (id) => axios.get(`${API}/rentals/items/${id}`),
  createRentalItem: (data) => axios.post(`${API}/rentals/items`, data),
  updateRentalItem: (id, data) => axios.put(`${API}/rentals/items/${id}`, data),
  deleteRentalItem: (id) => axios.delete(`${API}/rentals/items/${id}`),
  getRentalCategories: () => axios.get(`${API}/rentals/categories`),
  getItemAvailability: (id, startDate, endDate) => axios.get(`${API}/rentals/items/${id}/availability`, { params: { start_date: startDate, end_date: endDate } }),
  updateItemAvailability: (id, dates, isAvailable) => axios.put(`${API}/rentals/items/${id}/availability`, null, { params: { dates, is_available: isAvailable } }),
  createRentalRequest: (itemId, data) => axios.post(`${API}/rentals/items/${itemId}/request`, data),
  getMyRentalRequests: () => axios.get(`${API}/rentals/requests/me`),
  getReceivedRentalRequests: () => axios.get(`${API}/rentals/requests/received`),
  respondToRentalRequest: (requestId, accept) => axios.post(`${API}/rentals/requests/${requestId}/respond`, null, { params: { accept } }),
  getMyRentalItems: (params) => axios.get(`${API}/rentals/my/items`, { params }),
  getMyRentalStats: () => axios.get(`${API}/rentals/my/stats`),
  
  // Admin
  adminGetUsers: (params) => axios.get(`${API}/admin/users`, { params }),
  adminGetCompanies: (params) => axios.get(`${API}/admin/companies`, { params }),
  adminVerifyUser: (userId) => axios.post(`${API}/admin/users/${userId}/verify`),
  adminBlockUser: (userId) => axios.post(`${API}/admin/users/${userId}/block`),
  adminGetPayments: (params) => axios.get(`${API}/admin/payments`, { params }),
  adminGetSubscriptions: () => axios.get(`${API}/admin/subscriptions`),
  
  // Admin Dashboard Stats
  adminGetDashboardStats: () => axios.get(`${API}/admin/dashboard/stats`),
  adminGetRecentActivity: (params) => axios.get(`${API}/admin/dashboard/recent-activity`, { params }),
  
  // Admin Audit Logs
  adminGetAuditLogs: (params) => axios.get(`${API}/admin/audit-logs`, { params }),
  adminGetAuditLogSummary: (params) => axios.get(`${API}/admin/audit-logs/summary`, { params }),
  adminGetAuditActionTypes: () => axios.get(`${API}/admin/audit-logs/action-types`),
  adminCleanupAuditLogs: () => axios.post(`${API}/admin/audit-logs/cleanup`),
  
  // Company Audit Logs (for company managers)
  companyGetAuditLogs: (params) => axios.get(`${API}/admin/company/audit-logs`, { params }),
  companyGetAuditLogSummary: (params) => axios.get(`${API}/admin/company/audit-logs/summary`, { params }),
  
  // Public Listings & Sharing
  getPublicJob: (slug) => axios.get(`${API}/public/jobs/${slug}`),
  getPublicService: (slug) => axios.get(`${API}/public/services/${slug}`),
  getPublicRental: (slug) => axios.get(`${API}/public/rentals/${slug}`),
  getPublicAutoService: (slug) => axios.get(`${API}/public/auto/${slug}`),
  trackShare: (entityType, entityId, platform) => axios.post(`${API}/public/share/${entityType}/${entityId}?platform=${platform}`),
  getShareStats: (entityType, entityId) => axios.get(`${API}/public/share-stats/${entityType}/${entityId}`),
  getSeoContent: (entityType, entityId, language) => axios.get(`${API}/public/seo-content/${entityType}/${entityId}`, { params: { language } }),
  
  // AI Services
  aiJobWriter: (data) => axios.post(`${API}/ai/job-writer`, data),
  aiCvBuilder: (data) => axios.post(`${API}/ai/cv-builder`, data),
  aiPriceEngine: (data) => axios.post(`${API}/ai/price-engine`, data),
  aiJobMatcher: (data) => axios.post(`${API}/ai/job-matcher`, data),
  aiBestJobs: () => axios.get(`${API}/ai/best-jobs`),
  // NOTE: generic get/post/put/patch/delete are declared once at the top of
  // this object (line ~265) with proper axios config passthrough. Do not
  // redeclare them here.
};

// ============================================
// CONSTANTS
// ============================================
export const JOB_CATEGORIES = [
  'construction', 'welding', 'plumbing', 'electrical', 'roofing',
  'carpentry', 'painting', 'cleaning', 'landscaping', 'transportation',
  'warehouse', 'manufacturing', 'hvac', 'masonry', 'flooring'
];

export const JOB_DURATIONS = [
  { value: '1-2_days', labelKey: 'durations.1-2_days' },
  { value: '3-4_days', labelKey: 'durations.3-4_days' },
  { value: '5-6_days', labelKey: 'durations.5-6_days' },
  { value: '7-10_days', labelKey: 'durations.7-10_days' },
  { value: 'full_time', labelKey: 'durations.full_time' }
];

export const COUNTRIES = [
  { code: 'EE', nameKey: 'countries.EE', flag: '🇪🇪' },
  { code: 'FI', nameKey: 'countries.FI', flag: '🇫🇮' },
  { code: 'SE', nameKey: 'countries.SE', flag: '🇸🇪' },
  { code: 'NO', nameKey: 'countries.NO', flag: '🇳🇴' },
  { code: 'LV', nameKey: 'countries.LV', flag: '🇱🇻' },
  { code: 'LT', nameKey: 'countries.LT', flag: '🇱🇹' },
  { code: 'DE', nameKey: 'countries.DE', flag: '🇩🇪' },
  { code: 'PL', nameKey: 'countries.PL', flag: '🇵🇱' },
  { code: 'DK', nameKey: 'countries.DK', flag: '🇩🇰' }
];

export const AUTO_REPAIR_TYPES = [
  'tire_change', 'engine', 'bodywork', 'brakes', 'electrical',
  'suspension', 'exhaust', 'transmission', 'air_conditioning', 'general_service'
];

export const TOOL_TYPES = [
  'power_drill', 'circular_saw', 'angle_grinder', 'welder', 'compressor',
  'scaffolding', 'ladder', 'excavator', 'concrete_mixer', 'generator',
  'pressure_washer', 'nail_gun', 'tile_cutter', 'demolition_hammer'
];


// ============================================================
// CONVERSION TRACKING UTILITIES
// ============================================================

const CONVERSION_STORAGE_KEY = 'keikka_conversion_source';

/**
 * Store conversion source from URL parameters or referrer
 * Call this when landing on a shared page
 */
export const storeConversionSource = (entityType, entityId, platform = null) => {
  const source = {
    entityType,
    entityId,
    platform,
    referrer: document.referrer,
    landingPage: window.location.href,
    timestamp: new Date().toISOString()
  };
  sessionStorage.setItem(CONVERSION_STORAGE_KEY, JSON.stringify(source));
};

/**
 * Get stored conversion source
 */
export const getConversionSource = () => {
  try {
    const stored = sessionStorage.getItem(CONVERSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/**
 * Clear conversion source after successful conversion
 */
export const clearConversionSource = () => {
  sessionStorage.removeItem(CONVERSION_STORAGE_KEY);
};

/**
 * Get headers for API calls that should track conversions
 */
export const getConversionHeaders = () => {
  const source = getConversionSource();
  if (!source) return {};
  
  return {
    'X-Source-Platform': source.platform || '',
    'X-Source-Entity-Type': source.entityType || '',
    'X-Source-Entity-Id': source.entityId || '',
    'X-Source-Click-Id': source.clickId || ''
  };
};

/**
 * Detect if user arrived from a social platform based on referrer
 */
export const detectSourcePlatform = () => {
  const referrer = document.referrer.toLowerCase();
  const url = new URL(window.location.href);
  
  // Check URL parameters first (UTM tags)
  const utmSource = url.searchParams.get('utm_source');
  const utmMedium = url.searchParams.get('utm_medium');
  
  if (utmSource) {
    const utmLower = utmSource.toLowerCase();
    if (utmLower.includes('facebook') || utmLower.includes('fb')) return 'facebook';
    if (utmLower.includes('linkedin')) return 'linkedin';
    if (utmLower.includes('twitter') || utmLower.includes('x.com')) return 'twitter';
    if (utmLower.includes('tiktok')) return 'tiktok';
    if (utmLower.includes('whatsapp')) return 'whatsapp';
    if (utmLower.includes('email') || utmMedium === 'email') return 'email';
    if (utmLower.includes('google')) return 'google';
  }
  
  // If no UTM, check referrer
  if (!referrer) {
    return 'direct'; // No referrer = direct traffic
  }
  
  const platformPatterns = {
    facebook: /facebook\.com|fb\.com|fbcdn\.net|l\.facebook\.com/,
    linkedin: /linkedin\.com|lnkd\.in/,
    twitter: /twitter\.com|t\.co|x\.com/,
    whatsapp: /whatsapp\.com|wa\.me|web\.whatsapp/,
    tiktok: /tiktok\.com|tiktokcdn\.com/,
    messenger: /messenger\.com|m\.me/,
    google: /google\.|googleapis\.com|googleusercontent\.com/,
    bing: /bing\.com/,
    yahoo: /yahoo\.com|search\.yahoo/,
    email: /mail\.google\.com|outlook\.|mail\.yahoo|webmail|mailchimp|sendgrid/
  };
  
  for (const [platform, pattern] of Object.entries(platformPatterns)) {
    if (pattern.test(referrer)) {
      return platform;
    }
  }
  
  // Check if it's internal traffic (same domain)
  try {
    const referrerHost = new URL(referrer).hostname;
    const currentHost = window.location.hostname;
    if (referrerHost === currentHost || referrerHost.endsWith(currentHost)) {
      return 'internal';
    }
  } catch {
    // Invalid referrer URL
  }
  
  return 'other'; // Unknown external source
};

/**
 * Get detailed traffic source information
 */
export const getTrafficSource = () => {
  const platform = detectSourcePlatform();
  const referrer = document.referrer;
  const url = new URL(window.location.href);
  
  return {
    platform,
    referrer,
    landingPage: window.location.pathname,
    utmSource: url.searchParams.get('utm_source'),
    utmMedium: url.searchParams.get('utm_medium'),
    utmCampaign: url.searchParams.get('utm_campaign'),
    timestamp: new Date().toISOString()
  };
};

/**
 * Auto-detect and store source on page load
 * Should be called once when the app initializes
 */
export const autoDetectAndStoreSource = () => {
  // Don't overwrite if already stored in this session
  const existing = getConversionSource();
  if (existing && existing.platform) {
    return existing;
  }
  
  const platform = detectSourcePlatform();
  
  // Only store if user came from an external source
  if (platform && platform !== 'internal') {
    const url = new URL(window.location.href);
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    
    // Try to detect entity type and ID from URL
    let entityType = null;
    let entityId = null;
    
    const entityRoutes = ['jobs', 'rentals', 'auto', 'services', 'subcontracting'];
    if (pathParts.length >= 2 && entityRoutes.includes(pathParts[0])) {
      entityType = pathParts[0] === 'auto' ? 'auto_service' : 
                   pathParts[0] === 'subcontracting' ? 'subcontract' :
                   pathParts[0] === 'rentals' ? 'rental' :
                   pathParts[0].replace(/s$/, ''); // Remove trailing 's'
      entityId = pathParts[1];
    }
    
    const source = {
      platform,
      entityType,
      entityId,
      referrer: document.referrer,
      landingPage: window.location.pathname,
      utmSource: url.searchParams.get('utm_source'),
      utmMedium: url.searchParams.get('utm_medium'),
      utmCampaign: url.searchParams.get('utm_campaign'),
      timestamp: new Date().toISOString()
    };
    
    sessionStorage.setItem(CONVERSION_STORAGE_KEY, JSON.stringify(source));
    
    // Log for debugging (remove in production)
    console.log('[KEIKKA] Source detected:', platform, source);
    
    return source;
  }
  
  return null;
};

/**
 * Check if user has trackable source (for conversion attribution)
 */
export const hasTrackableSource = () => {
  const source = getConversionSource();
  return source && source.platform && source.platform !== 'internal' && source.platform !== 'direct';
};
